import { useMutation } from '@apollo/client';
import type { UpsertUserTablePreferencesMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpsertUserTablePreferencesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import {
  type UpsertUserTablePreferencesInput,
  useUpsertUserTablePreferencesTRPC,
} from './useUpsertUserTablePreferencesTRPC';

/**
 * Flag-gated wrapper for the `upsertUserTablePreferences` mutation. Defaults to
 * the legacy Apollo mutation and delegates to the tRPC variant when the `trpc`
 * feature flag is enabled. Both paths return the same
 * `UpsertUserTablePreferencesMutation` shape so the consumer doesn't branch.
 */
export const useUpsertUserTablePreferences = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [upsertGraphQL, graphqlState] = useMutation(
    UpsertUserTablePreferencesDocument
  );

  const trpcMutation = useUpsertUserTablePreferencesTRPC();

  const upsertUserTablePreferences = async (
    input: UpsertUserTablePreferencesInput
  ): Promise<UpsertUserTablePreferencesMutation> => {
    if (trpcEnabled) {
      return trpcMutation.upsertUserTablePreferences(input);
    }

    const result = await upsertGraphQL({
      variables: { TableId: input.TableId, Preferences: input.Preferences },
    });
    if (!result.data) {
      throw new Error('Failed to upsert user table preferences');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      upsertUserTablePreferences,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    upsertUserTablePreferences,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
